import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext.jsx';
import { FaPencilAlt, FaTrash, FaUpload, FaPlus, FaSearch, FaBox, FaWarehouse, FaTag, FaExclamationTriangle, FaCheckCircle, FaCog, FaEye, FaTimes, FaHistory, FaCalendar, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import * as XLSX from 'xlsx';


// Auto-generate SKU function
const generateSKU = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `SKU-${year}${month}${day}-${hours}${minutes}${seconds}`;
};

function Inventory() {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  // Helper to get correct auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };

  const [items, setItems] = useState([]);
  
  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    subBusiness: 'Salon',
    description: '',
    sku: generateSKU(),
    quantity: 0,
    sellingPrice: '',
    purchasePrice: '',
    barcode: '',
    reorderPoint: '',
    checkbox: false,
    discount: 0,
  });
  
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubBusiness, setFilterSubBusiness] = useState('');
  const [filterStock, setFilterStock] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const itemsPerPage = 18;
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // ✅ NEW: Success notification states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [addedItemDetails, setAddedItemDetails] = useState(null);

  // ✅ NEW: History states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyType, setHistoryType] = useState('global'); // 'global' or 'item'
  const [selectedItemForHistory, setSelectedItemForHistory] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    subBusiness: ''
  });

  const generateDummyBarcode = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `BC${timestamp}${random}`;
  };

  // ✅ CHANGE 1: Simplified category loading (returns objects with _id, name, subBusiness)
  useEffect(() => {
    const fetchData = async () => {
      console.log('Axios default Authorization header:', axios.defaults.headers.common['Authorization']);
      setLoading(true);
      try {
        const [inventoryRes, categoriesRes] = await Promise.all([
          axios.get(`${import.meta.env.VITE_API_URL}/api/inventory`),
          axios.get(`${import.meta.env.VITE_API_URL}/api/categories`),
        ]);
        setItems(Array.isArray(inventoryRes.data) ? inventoryRes.data : []);
        setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []); // ✅ Now stores full objects
        setError('');
      } catch (error) {
        console.error('Failed to load data:', error.response?.data || error.message);
        if (error.response?.status === 401) {
          logout();
          navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
        } else {
          setError(`Failed to load data: ${error.response?.data?.message || error.message}`);
          setItems([]);
          setCategories([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // ✅ NEW: Fetch global history
  const fetchGlobalHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (historyFilters.subBusiness && historyFilters.subBusiness !== 'All') {
        params.subBusiness = historyFilters.subBusiness;
      }
      if (historyFilters.startDate) params.startDate = historyFilters.startDate;
      if (historyFilters.endDate) params.endDate = historyFilters.endDate;
      if (historyFilters.search) params.search = historyFilters.search;

      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/inventory/logs/all`, { params });
      setHistoryData(response.data);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ✅ NEW: Fetch item-specific history
  const fetchItemHistory = async (itemId) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/inventory/${itemId}/logs`);
      setHistoryData(response.data);
    } catch (err) {
      console.error('Error fetching item history:', err);
      setError('Failed to load item history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ✅ NEW: Open global history modal
  const handleViewGlobalHistory = () => {
    setHistoryType('global');
    setSelectedItemForHistory(null);
    setShowHistoryModal(true);
    fetchGlobalHistory();
  };

  // ✅ NEW: Open item-specific history modal
  const handleViewItemHistory = (item) => {
    setHistoryType('item');
    setSelectedItemForHistory(item);
    setShowHistoryModal(true);
    fetchItemHistory(item._id);
  };

  // ✅ NEW: Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ✅ NEW: Export history to Excel
  const exportHistoryToExcel = () => {
    const exportData = historyData.map(log => ({
      'Date': formatDate(log.date),
      'Item Name': log.itemName,
      'SKU': log.itemSku,
      'Sub-Business': log.subBusiness,
      'Previous Qty': log.previousQty,
      'Quantity Added': log.quantityAdded,
      'New Total': log.newQty
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory History');
    XLSX.writeFile(wb, `inventory_history_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.sku || newItem.quantity < 0) {
      setError('Required fields: Name, SKU, and Quantity must be valid.');
      return;
    }
    if (newItem.checkbox && (!newItem.sellingPrice || newItem.sellingPrice <= 0)) {
      setError('Selling price is required and must be greater than 0 for items available for sale.');
      return;
    }
    try {
      const itemToAdd = {
        name: newItem.name,
        sku: newItem.sku,
        quantity: newItem.quantity,
        subBusiness: newItem.subBusiness,
        category: newItem.category || undefined, // ✅ Now sends ObjectId (or undefined)
        sellingPrice: newItem.checkbox ? parseFloat(newItem.sellingPrice) : undefined,
        purchasePrice: newItem.purchasePrice ? parseFloat(newItem.purchasePrice) : undefined,
        barcode: newItem.barcode || generateDummyBarcode(),
        reorderPoint: newItem.reorderPoint ? parseInt(newItem.reorderPoint) : undefined,
        description: newItem.description || undefined,
        checkbox: newItem.checkbox,
        discount: newItem.discount || 0,
      };
      
      const res = await axios.post(`${import.meta.env.VITE_API_URL}/api/inventory`, itemToAdd);
      setItems([...items, res.data]);

      // ✅ NEW: Show success modal with item details
      setAddedItemDetails(res.data);
      setShowSuccessModal(true);

      // Reset form with NEW auto-generated SKU
      setNewItem({
        name: '',
        category: '',
        subBusiness: 'Salon',
        description: '',
        sku: generateSKU(),
        quantity: 0,
        sellingPrice: '',
        purchasePrice: '',
        barcode: '',
        reorderPoint: '',
        checkbox: false,
        discount: 0,
      });
      setError('');
    } catch (err) {
      console.error('Failed to add item:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else if (err.response?.status === 400 && err.response.data.message.includes('Duplicate')) {
        setError(err.response.data.message);
      } else if (err.response?.status === 400 && err.response.data.errors) {
        const errorMessages = Object.values(err.response.data.errors).map(e => e.message).join('; ');
        setError(`Validation error: ${errorMessages}`);
      } else {
        setError(`Failed to add item: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const handleUpdateItem = async () => {
  if (!editingItem.name || !editingItem.sku || editingItem.quantity < 0) {
    setError('Required fields: Name, SKU, and Quantity must be valid.');
    return;
  }
  if (editingItem.checkbox && (!editingItem.sellingPrice || editingItem.sellingPrice <= 0)) {
    setError('Selling price is required and must be greater than 0 for items available for sale.');
    return;
  }
  try {
    // ✅ FIXED: Properly handle purchasePrice clearing
    const itemToUpdate = {
      name: editingItem.name,
      sku: editingItem.sku,
      quantity: editingItem.quantity,
      subBusiness: editingItem.subBusiness,
      category: editingItem.category || undefined,
      sellingPrice: editingItem.checkbox ? parseFloat(editingItem.sellingPrice) : undefined,
      // ✅ CRITICAL FIX: Send null for empty string to clear the field
      purchasePrice: editingItem.purchasePrice === '' || editingItem.purchasePrice === null || editingItem.purchasePrice === undefined
        ? null 
        : parseFloat(editingItem.purchasePrice),
      barcode: editingItem.barcode || generateDummyBarcode(),
      reorderPoint: editingItem.reorderPoint ? parseInt(editingItem.reorderPoint) : undefined,
      description: editingItem.description || undefined,
      checkbox: editingItem.checkbox,
      discount: editingItem.discount || 0,
    };
    
    const res = await axios.put(`${import.meta.env.VITE_API_URL}/api/inventory/${editingItemId}`, itemToUpdate);
    setItems(items.map(item => (item._id === editingItemId ? res.data : item)));

    setEditingItemId(null);
    setEditingItem(null);
    setError('');
  } catch (err) {
    console.error('Failed to update item:', err.response?.data || err.message);
    if (err.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
    } else if (err.response?.status === 400 && err.response.data.message.includes('Duplicate')) {
      setError(err.response.data.message);
    } else if (err.response?.status === 400 && err.response.data.errors) {
      const errorMessages = Object.values(err.response.data.errors).map(e => e.message).join('; ');
      setError(`Validation error: ${errorMessages}`);
    } else {
      setError(`Failed to update item: ${err.response?.data?.message || err.message}`);
    }
  }
};

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItem(null);
    setError('');
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/inventory/${id}`);
      setItems(items.filter(item => item._id !== id));
      setError('');
    } catch (err) {
      console.error('Failed to delete item:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to delete item: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  // ✅ FIXED: Bulk upload now sends category as ObjectId
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);
        
        const errors = [];
        const validItems = [];
        
        json.forEach((row, index) => {
          const rowNum = index + 2;
          
          if (!row.name || !row.sku || row.quantity === undefined || !row.subBusiness) {
            errors.push(`Row ${rowNum}: Missing required fields (name, sku, quantity, subBusiness)`);
            return;
          }
          
          if (!['Salon', 'Spa', 'Gym'].includes(row.subBusiness)) {
            errors.push(`Row ${rowNum}: Invalid subBusiness "${row.subBusiness}". Must be Salon, Spa, or Gym.`);
            return;
          }
          
          // ✅ FIXED: Convert category name to ObjectId
          let categoryId = undefined;
          if (row.category) {
            const matchingCategory = categories.find(
              cat => cat.name.toLowerCase() === row.category.toLowerCase() && 
                     cat.subBusiness === row.subBusiness
            );
            if (matchingCategory) {
              categoryId = matchingCategory._id;
            } else {
              errors.push(`Row ${rowNum}: Category "${row.category}" not found for ${row.subBusiness}. Create it first or leave blank.`);
              return;
            }
          }
          
          validItems.push({
            name: row.name,
            sku: row.sku,
            quantity: parseInt(row.quantity) || 0,
            subBusiness: row.subBusiness,
            category: categoryId,
            barcode: row.barcode || generateDummyBarcode(),
            sellingPrice: row.sellingPrice ? parseFloat(row.sellingPrice) : undefined,
            purchasePrice: row.purchasePrice ? parseFloat(row.purchasePrice) : undefined,
            reorderPoint: row.reorderPoint ? parseInt(row.reorderPoint) : undefined,
            description: row.description || undefined,
            checkbox: false,
            discount: row.discount ? parseFloat(row.discount) : 0,
          });
        });
        
        setUploadItems(validItems);
        setUploadErrors(errors);
        setShowUploadModal(true);
        
      } catch (err) {
        console.error('Error parsing file:', err);
        setError('Failed to parse file. Ensure it is a valid CSV or XLSX file.');
      }
    };
    
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  const handleConfirmUpload = async () => {
    setUploadLoading(true);
    try {
      const promises = uploadItems.map(item => 
        axios.post(`${import.meta.env.VITE_API_URL}/api/inventory`, item)
      );
      const results = await Promise.all(promises);
      setItems([...items, ...results.map(res => res.data)]);
      
      setShowUploadModal(false);
      setUploadItems([]);
      setUploadErrors([]);
      setError('');
    } catch (err) {
      console.error('Failed to upload items:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to upload items: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setError('Category name cannot be empty.');
      return;
    }
    
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_URL}/api/categories`, {
        name: newCategory.trim(),
        subBusiness: newItem.subBusiness
      });
      
      setCategories([...categories, res.data]);
      setNewItem({ ...newItem, category: res.data._id }); // ✅ Now sets ObjectId
      setNewCategory('');
      setShowNewCategoryInput(false);
      setError('');
    } catch (err) {
      console.error('Failed to add category:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to add category: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory.name.trim()) {
      setError('Category name cannot be empty.');
      return;
    }
    
    try {
      const res = await axios.put(
        `${import.meta.env.VITE_API_URL}/api/categories/${editingCategory._id}`,
        {
          name: editingCategory.name.trim(),
          subBusiness: editingCategory.subBusiness
        }
      );
      
      setCategories(categories.map(cat => cat._id === editingCategory._id ? res.data : cat));
      setEditingCategory(null);
      setError('');
    } catch (err) {
      console.error('Failed to update category:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to update category: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete category "${name}"?`)) return;
    
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/categories/${id}`);
      setCategories(categories.filter(cat => cat._id !== id));
      setError('');
    } catch (err) {
      console.error('Failed to delete category:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to delete category: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const getStockStatus = (item) => {
    if (!item.reorderPoint) return { status: 'normal', color: 'text-green-600', bg: 'bg-green-100' };
    if (item.quantity === 0) return { status: 'out', color: 'text-red-600', bg: 'bg-red-100' };
    if (item.quantity < item.reorderPoint) return { status: 'low', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'normal', color: 'text-green-600', bg: 'bg-green-100' };
  };

  const getSectionColor = (section) => {
    const colors = {
      Salon: 'bg-purple-100 text-purple-600',
      Spa: 'bg-blue-100 text-blue-600',
      Gym: 'bg-green-100 text-green-600'
    };
    return colors[section] || 'bg-gray-100 text-gray-600';
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
                         item.sku.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !filterCategory || 
                           (item.category?._id === filterCategory);
    const matchesSubBusiness = !filterSubBusiness || item.subBusiness === filterSubBusiness;
    const matchesStock = 
      !filterStock || 
      (filterStock === 'low' && item.reorderPoint && item.quantity < item.reorderPoint) ||
      (filterStock === 'out' && item.quantity === 0) ||
      (filterStock === 'normal' && (!item.reorderPoint || item.quantity >= item.reorderPoint));
    
    return matchesSearch && matchesCategory && matchesSubBusiness && matchesStock;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // ✅ NEW: Filter categories based on selected sub-business
  const getFilteredCategoriesForSubBusiness = (subBusiness) => {
    return categories.filter(cat => cat.subBusiness === subBusiness);
  };

  // Helper functions for stats
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const adjustQuantity = (delta, isEditing = false) => {
    if (isEditing) {
      const newQty = Math.max(0, editingItem.quantity + delta);
      setEditingItem({ ...editingItem, quantity: newQty });
    } else {
      const newQty = Math.max(0, newItem.quantity + delta);
      setNewItem({ ...newItem, quantity: newQty });
    }
  };

  // Calculate stats
  const totalItems = items.length;
  const lowStockItems = items.filter(item => item.reorderPoint && item.quantity <= item.reorderPoint).length;
  const outOfStockItems = items.filter(item => item.quantity === 0).length;
  const totalValue = items.reduce((acc, item) => acc + (item.sellingPrice || 0) * item.quantity, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-2">Inventory Management</h1>
          <p className="text-slate-600">Track and manage your inventory items</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg flex-shrink-0">
                <FaBox className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-600 truncate">Total Items</p>
                <p className="text-2xl font-bold text-slate-900 truncate">{totalItems}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg flex-shrink-0">
                <FaExclamationTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-600 truncate">Low Stock</p>
                <p className="text-2xl font-bold text-slate-900 truncate">{lowStockItems}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg flex-shrink-0">
                <FaWarehouse className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-600 truncate">Out of Stock</p>
                <p className="text-2xl font-bold text-slate-900 truncate">{outOfStockItems}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                <FaTag className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-600 truncate">Total Value</p>
                <p className="text-xl font-bold text-slate-900 truncate break-all">{formatPrice(totalValue)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex justify-end space-x-3">
          <button
            onClick={handleViewGlobalHistory}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <FaHistory className="mr-2" />
            View History
          </button>
          <button
            onClick={() => setShowCategoriesModal(true)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            <FaCog className="mr-2" />
            Manage Categories
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Add/Edit Item Form */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
            <div className="flex items-center">
              <FaPlus className="text-white mr-3" />
              <h2 className="text-xl font-semibold text-white">{editingItemId ? 'Edit Item' : 'Add New Item'}</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  placeholder="Item Name"
                  value={editingItem ? editingItem.name : newItem.name}
                  onChange={(e) => editingItem
                    ? setEditingItem({ ...editingItem, name: e.target.value })
                    : setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  SKU * {!editingItem && <span className="text-xs text-purple-600">(Auto-generated)</span>}
                </label>
                <input
                  type="text"
                  placeholder="SKU (Auto-generated)"
                  value={editingItem ? editingItem.sku : newItem.sku}
                  onChange={(e) => editingItem
                    ? setEditingItem({ ...editingItem, sku: e.target.value })
                    : setNewItem({ ...newItem, sku: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-slate-50"
                  readOnly={!editingItem}
                  disabled={!editingItem}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Quantity *</label>
                <div className="flex items-center">
                  <button
                    onClick={() => adjustQuantity(-1, !!editingItem)}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-l-lg hover:bg-gray-300"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={editingItem ? editingItem.quantity : newItem.quantity}
                    onChange={(e) => editingItem
                      ? setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 0 })
                      : setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center"
                  />
                  <button
                    onClick={() => adjustQuantity(1, !!editingItem)}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-r-lg hover:bg-gray-300"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sub-Business</label>
                <select
                  value={editingItem ? editingItem.subBusiness : newItem.subBusiness}
                  onChange={(e) => {
                    const newSubBusiness = e.target.value;
                    if (editingItem) {
                      setEditingItem({ ...editingItem, subBusiness: newSubBusiness, category: '' });
                    } else {
                      setNewItem({ ...newItem, subBusiness: newSubBusiness, category: '' });
                    }
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="Salon">Salon</option>
                  <option value="Spa">Spa</option>
                  <option value="Gym">Gym</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                <div className="flex space-x-2">
                  <select
                    value={editingItem ? editingItem.category : newItem.category}
                    onChange={(e) => editingItem
                      ? setEditingItem({ ...editingItem, category: e.target.value })
                      : setNewItem({ ...newItem, category: e.target.value })}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select Category (Optional)</option>
                    {getFilteredCategoriesForSubBusiness(editingItem ? editingItem.subBusiness : newItem.subBusiness).map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowNewCategoryInput(!showNewCategoryInput)}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    title="Add new category"
                  >
                    <FaPlus />
                  </button>
                </div>
                {showNewCategoryInput && (
                  <div className="mt-2">
                    <input
                      type="text"
                      placeholder="New Category Name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-2"
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleAddCategory}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        Add Category
                      </button>
                      <button
                        onClick={() => {
                          setShowNewCategoryInput(false);
                          setNewCategory('');
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Purchase Price (₦)</label>
                <input
                  type="number"
                  placeholder="Purchase Price"
                  value={editingItem ? editingItem.purchasePrice : newItem.purchasePrice}
                  onChange={(e) => editingItem
                    ? setEditingItem({ ...editingItem, purchasePrice: e.target.value })
                    : setNewItem({ ...newItem, purchasePrice: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              {/* ✅ COMMENTED OUT: Barcode field - will be brought back in future
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Barcode</label>
                <input
                  type="text"
                  placeholder="Barcode (Optional)"
                  value={editingItem ? editingItem.barcode : newItem.barcode}
                  onChange={(e) => editingItem
                    ? setEditingItem({ ...editingItem, barcode: e.target.value })
                    : setNewItem({ ...newItem, barcode: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              */}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Reorder Point</label>
                <input
                  type="number"
                  placeholder="Reorder Point"
                  value={editingItem ? editingItem.reorderPoint : newItem.reorderPoint}
                  onChange={(e) => editingItem
                    ? setEditingItem({ ...editingItem, reorderPoint: e.target.value })
                    : setNewItem({ ...newItem, reorderPoint: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingItem ? editingItem.checkbox : newItem.checkbox}
                    onChange={(e) => editingItem
                      ? setEditingItem({ ...editingItem, checkbox: e.target.checked })
                      : setNewItem({ ...newItem, checkbox: e.target.checked })}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Available for Sale</span>
                </label>
              </div>
              
              {(editingItem ? editingItem.checkbox : newItem.checkbox) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Selling Price (₦) *</label>
                    <input
                      type="number"
                      placeholder="Selling Price"
                      value={editingItem ? editingItem.sellingPrice : newItem.sellingPrice}
                      onChange={(e) => editingItem
                        ? setEditingItem({ ...editingItem, sellingPrice: e.target.value })
                        : setNewItem({ ...newItem, sellingPrice: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Discount (%)</label>
                    <input
                      type="number"
                      placeholder="Discount (%)"
                      value={editingItem ? editingItem.discount : newItem.discount}
                      onChange={(e) => editingItem
                        ? setEditingItem({ ...editingItem, discount: parseFloat(e.target.value) || 0 })
                        : setNewItem({ ...newItem, discount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
              
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <input
                  type="text"
                  placeholder="Description (Optional)"
                  value={editingItem ? editingItem.description : newItem.description}
                  onChange={(e) => editingItem
                    ? setEditingItem({ ...editingItem, description: e.target.value })
                    : setNewItem({ ...newItem, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={editingItemId ? handleUpdateItem : handleAddItem}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
              >
                <FaPlus className="mr-2" />
                {editingItemId ? 'Update Item' : 'Add Item'}
              </button>
              {editingItemId && (
                <button
                  onClick={handleCancelEdit}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or SKU"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={filterSubBusiness}
              onChange={(e) => {
                setFilterSubBusiness(e.target.value);
                setFilterCategory('');
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Sub-Businesses</option>
              <option value="Salon">Salon</option>
              <option value="Spa">Spa</option>
              <option value="Gym">Gym</option>
            </select>
            
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {(filterSubBusiness 
                ? categories.filter(cat => cat.subBusiness === filterSubBusiness)
                : categories
              ).map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.name} ({cat.subBusiness})
                </option>
              ))}
            </select>
            
            <select
              value={filterStock}
              onChange={(e) => {
                setFilterStock(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Stock Levels</option>
              <option value="normal">Normal Stock</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
            </select>
          </div>
        </div>

        {/* Items Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center">
              <FaBox className="mr-2 text-purple-600" />
              Inventory Items ({filteredItems.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading inventory...</div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No items found</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedItems.map(item => {
                const isEditing = editingItemId === item._id;
                const stockStatus = getStockStatus(item);
                
                return (
                  <div key={item._id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition">
                    {isEditing ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editingItem.name}
                          onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Item Name"
                        />
                        <input
                          type="text"
                          value={editingItem.sku}
                          onChange={(e) => setEditingItem({ ...editingItem, sku: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="SKU"
                        />
                        <input
                          type="number"
                          value={editingItem.quantity}
                          onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Quantity"
                        />
                        <select
                          value={editingItem.subBusiness}
                          onChange={(e) => setEditingItem({ ...editingItem, subBusiness: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        >
                          <option value="Salon">Salon</option>
                          <option value="Spa">Spa</option>
                          <option value="Gym">Gym</option>
                        </select>
                        <select
                          value={editingItem.category || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        >
                          <option value="">No Category</option>
                          {getFilteredCategoriesForSubBusiness(editingItem.subBusiness).map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={editingItem.sellingPrice || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, sellingPrice: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Selling Price"
                        />
                        <input
                          type="number"
                          value={editingItem.purchasePrice || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, purchasePrice: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Purchase Price"
                        />
                        {/* ✅ COMMENTED OUT: Barcode field - will be brought back in future
                        <input
                          type="text"
                          value={editingItem.barcode || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, barcode: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Barcode"
                        />
                        */}
                        <input
                          type="number"
                          value={editingItem.reorderPoint || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, reorderPoint: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Reorder Point"
                        />
                        <input
                          type="number"
                          value={editingItem.discount || 0}
                          onChange={(e) => setEditingItem({ ...editingItem, discount: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Discount (%)"
                        />
                        <textarea
                          value={editingItem.description || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="Description"
                          rows="2"
                        />
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editingItem.checkbox}
                            onChange={(e) => setEditingItem({ ...editingItem, checkbox: e.target.checked })}
                            className="w-5 h-5 text-purple-600"
                          />
                          <span className="text-sm">Available for Sale</span>
                        </label>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleUpdateItem}
                            className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingItemId(null);
                              setEditingItem(null);
                            }}
                            className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-slate-900 break-words flex-1 pr-2">{item.name}</h3>
                          <div className="flex space-x-2 flex-shrink-0">
                            {/* ✅ NEW: View Item History Button */}
                            <button
                              onClick={() => handleViewItemHistory(item)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View history"
                            >
                              <FaHistory />
                            </button>
                            <button
                              onClick={() => {
                                setEditingItemId(item._id);
                                setEditingItem({
                                  name: item.name,
                                  sku: item.sku,
                                  quantity: item.quantity,
                                  subBusiness: item.subBusiness,
                                  category: item.category?._id || '',
                                  sellingPrice: item.sellingPrice || '',
                                  purchasePrice: item.purchasePrice || '',
                                  barcode: item.barcode || '',
                                  reorderPoint: item.reorderPoint || '',
                                  description: item.description || '',
                                  checkbox: item.checkbox,
                                  discount: item.discount || 0
                                });
                              }}
                              className="text-purple-600 hover:text-purple-800"
                              title="Edit"
                            >
                              <FaPencilAlt />
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p><strong>SKU:</strong> {item.sku}</p>
                          <p><strong>Quantity:</strong> {item.quantity}</p>
                          {item.category && (
                            <p className="flex items-center">
                              <FaTag className="mr-1" />
                              {item.category.name}
                            </p>
                          )}
                          {item.sellingPrice && (
                            <p><strong>Selling Price:</strong> ₦{item.sellingPrice.toFixed(2)}</p>
                          )}
                          {item.purchasePrice && (
                            <p><strong>Purchase Price:</strong> ₦{item.purchasePrice.toFixed(2)}</p>
                          )}
                          {item.discount > 0 && (
                            <p><strong>Discount:</strong> {item.discount}%</p>
                          )}
                          {item.reorderPoint && (
                            <p><strong>Reorder Point:</strong> {item.reorderPoint}</p>
                          )}
                          {item.checkbox && (
                            <p className="flex items-center text-green-600">
                              <FaCheckCircle className="mr-1" />
                              Available for Sale
                            </p>
                          )}
                        </div>
                        <div className="mt-3 flex justify-between items-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionColor(item.subBusiness)}`}>
                            {item.subBusiness}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${stockStatus.bg} ${stockStatus.color}`}>
                            {stockStatus.status.charAt(0).toUpperCase() + stockStatus.status.slice(1)} Stock
                          </span>
                        </div>
                        {/* ✅ REMOVED: Barcode display - will be brought back in future */}
                        {item.description && (
                          <p className="mt-2 text-sm text-slate-600 break-words line-clamp-2" title={item.description}>
                            {item.description}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Bulk Upload Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center mb-4">
            <FaUpload className="text-purple-600 mr-3" />
            <h2 className="text-xl font-semibold text-slate-800">Bulk Upload Items</h2>
          </div>
          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-700">
              <strong>Upload a CSV or XLSX file with columns:</strong> name, sku, quantity, subBusiness. 
              <br />
              <strong>Optional columns:</strong> category, barcode, sellingPrice, purchasePrice, reorderPoint, description, discount.
              <br />
              <strong>Note:</strong> subBusiness must be one of: Salon, Spa, Gym. Category must match existing categories for that subBusiness.
            </p>
          </div>
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={handleBulkUpload}
            className="w-full sm:w-64 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {uploadLoading && <p className="text-purple-600 mt-2">Uploading...</p>}
        </div>

        {/* Upload Confirmation Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Confirm Bulk Upload</h2>
            {uploadErrors.length > 0 && (
              <div className="mb-4">
                <h3 className="text-red-600 font-medium">Errors Found:</h3>
                <ul className="list-disc pl-5 text-red-500 text-sm">
                  {uploadErrors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
            <h3 className="font-medium mb-2">Items to Upload: {uploadItems.length}</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="p-2 border-b text-left">Name</th>
                    <th className="p-2 border-b text-left">SKU</th>
                    <th className="p-2 border-b text-left">Quantity</th>
                    <th className="p-2 border-b text-left">Sub-Business</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadItems.map((item, index) => (
                    <tr key={index}>
                      <td className="p-2 border-b truncate max-w-[150px]">{item.name}</td>
                      <td className="p-2 border-b">{item.sku}</td>
                      <td className="p-2 border-b">{item.quantity}</td>
                      <td className="p-2 border-b">{item.subBusiness}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadItems([]);
                  setUploadErrors([]);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={uploadLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                {uploadLoading ? 'Uploading...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-2xl font-semibold text-slate-800 flex items-center">
                <FaHistory className="mr-3 text-blue-600" />
                {historyType === 'global' 
                  ? 'Inventory History - All Items' 
                  : `History: ${selectedItemForHistory?.name}`}
              </h2>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryData([]);
                  setHistoryFilters({ startDate: '', endDate: '', search: '', subBusiness: '' });
                }}
                className="text-slate-400 hover:text-slate-600 text-2xl"
              >
                <FaTimes />
              </button>
            </div>

            {/* Filters (only for global view) */}
            {historyType === 'global' && (
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="relative">
                    <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type="date"
                      value={historyFilters.startDate}
                      onChange={(e) => setHistoryFilters({ ...historyFilters, startDate: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Start Date"
                    />
                  </div>
                  <div className="relative">
                    <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type="date"
                      value={historyFilters.endDate}
                      onChange={(e) => setHistoryFilters({ ...historyFilters, endDate: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="End Date"
                    />
                  </div>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={historyFilters.search}
                      onChange={(e) => setHistoryFilters({ ...historyFilters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Search item name/SKU"
                    />
                  </div>
                  <select
                    value={historyFilters.subBusiness}
                    onChange={(e) => setHistoryFilters({ ...historyFilters, subBusiness: e.target.value })}
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Sub-Businesses</option>
                    <option value="Salon">Salon</option>
                    <option value="Spa">Spa</option>
                    <option value="Gym">Gym</option>
                  </select>
                </div>
                <div className="mt-3 flex space-x-3">
                  <button
                    onClick={fetchGlobalHistory}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <FaSearch className="mr-2" />
                    Apply Filters
                  </button>
                  <button
                    onClick={() => {
                      setHistoryFilters({ startDate: '', endDate: '', search: '', subBusiness: '' });
                      fetchGlobalHistory();
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={exportHistoryToExcel}
                    disabled={historyData.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <FaUpload className="mr-2" />
                    Export to Excel
                  </button>
                </div>
              </div>
            )}

            {/* History Table */}
            <div className="flex-1 overflow-y-auto p-6">
              {historyLoading ? (
                <div className="text-center py-8 text-slate-500">Loading history...</div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No history records found
                  {historyType === 'item' && ' for this item'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-slate-700 border-b">Date & Time</th>
                        {historyType === 'global' && (
                          <>
                            <th className="p-3 text-left text-sm font-semibold text-slate-700 border-b">Item Name</th>
                            <th className="p-3 text-left text-sm font-semibold text-slate-700 border-b">SKU</th>
                            <th className="p-3 text-left text-sm font-semibold text-slate-700 border-b">Sub-Business</th>
                          </>
                        )}
                        <th className="p-3 text-right text-sm font-semibold text-slate-700 border-b">Previous Qty</th>
                        <th className="p-3 text-right text-sm font-semibold text-slate-700 border-b">Qty Change</th>
                        <th className="p-3 text-right text-sm font-semibold text-slate-700 border-b">New Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((log, index) => (
                        <tr key={log._id || index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 text-sm text-slate-600 whitespace-nowrap">{formatDate(log.date)}</td>
                          {historyType === 'global' && (
                            <>
                              <td className="p-3 text-sm text-slate-900 font-medium">{log.itemName}</td>
                              <td className="p-3 text-sm text-slate-600">{log.itemSku}</td>
                              <td className="p-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${getSectionColor(log.subBusiness)}`}>
                                  {log.subBusiness}
                                </span>
                              </td>
                            </>
                          )}
                          <td className="p-3 text-sm text-slate-600 text-right">{log.previousQty}</td>
                          <td className="p-3 text-sm text-right">
                            <span className={`inline-flex items-center font-medium ${
                              log.quantityAdded > 0 ? 'text-green-600' : log.quantityAdded < 0 ? 'text-red-600' : 'text-slate-600'
                            }`}>
                              {log.quantityAdded > 0 ? (
                                <>
                                  <FaArrowUp className="mr-1" />
                                  +{log.quantityAdded}
                                </>
                              ) : log.quantityAdded < 0 ? (
                                <>
                                  <FaArrowDown className="mr-1" />
                                  {log.quantityAdded}
                                </>
                              ) : (
                                '0'
                              )}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-slate-900 font-semibold text-right">{log.newQty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 text-sm text-slate-600">
              Total Records: {historyData.length}
            </div>
          </div>
        </div>
      )}
 
      {/* ✅ Categories Modal */}
      {showCategoriesModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-800">Inventory Categories</h2>
              <button
                onClick={() => setShowCategoriesModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <FaTimes />
              </button>
            </div>
            {categories.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.map(cat => (
                  <div
                    key={cat._id}
                    className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{cat.name}</p>
                      <p className="text-sm text-slate-500">{cat.subBusiness}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setEditingCategory(cat);
                          setShowCategoriesModal(false);
                        }}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        <FaPencilAlt />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat._id, cat.name)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No categories found</p>
            )}
          </div>
        </div>
      )}

      {/* ✅ NEW: Success Notification Modal */}
      {showSuccessModal && addedItemDetails && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-bounce-in">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-3">
                <FaCheckCircle className="text-green-500 text-4xl" />
              </div>
              <h2 className="text-2xl font-bold text-white">Item Added Successfully!</h2>
            </div>
            
            {/* Item Details */}
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-600">Item Name:</span>
                  <span className="text-sm font-bold text-slate-900">{addedItemDetails.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-600">SKU:</span>
                  <span className="text-sm font-mono text-purple-600">{addedItemDetails.sku}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-600">Quantity:</span>
                  <span className="text-sm font-bold text-green-600">{addedItemDetails.quantity}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-600">Sub-Business:</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${getSectionColor(addedItemDetails.subBusiness)}`}>
                    {addedItemDetails.subBusiness}
                  </span>
                </div>
                {addedItemDetails.sellingPrice && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Selling Price:</span>
                    <span className="text-sm font-bold text-green-600">₦{addedItemDetails.sellingPrice.toFixed(2)}</span>
                  </div>
                )}
                {addedItemDetails.category && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Category:</span>
                    <span className="text-sm text-slate-900">{addedItemDetails.category.name}</span>
                  </div>
                )}
              </div>
              
              <div className="text-center text-sm text-slate-500">
                <p>Total Items: <span className="font-semibold text-purple-600">{items.length}</span></p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="p-6 bg-slate-50 border-t border-slate-200 flex space-x-3">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setAddedItemDetails(null);
                }}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition"
              >
                Add Another Item
              </button>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setAddedItemDetails(null);
                  // Scroll to the newly added item (it's at the end)
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }}
                className="flex-1 px-4 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 font-medium transition"
              >
                View Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Edit Category</h2>
            <div className="space-y-4">
              <input
                type="text"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Category Name"
              />
              <select
                value={editingCategory.subBusiness}
                onChange={(e) => setEditingCategory({ ...editingCategory, subBusiness: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="Salon">Salon</option>
                <option value="Spa">Spa</option>
                <option value="Gym">Gym</option>
              </select>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setEditingCategory(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCategory}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
export default Inventory;